import { useMemo } from "react";

import { AppleIxopaySafeBtn, IPaymentInfo, TAppleIxopayOptions } from "@kma-core/w2-w2w-payment-checkout";
import { NODE_ENV, PAYMENT_SERVER_URL, PROJECT_NAME } from "constants/env.const";
import { ampli } from "services/ampli";
import { useAppData, useUserStore } from "providers/RootStoreProvider";
import { createSearchParams, useNavigate } from "react-router-dom";
import { AppRoutes } from "enums/routes.enum";
import { IxopayFormData } from "components/Payment/shared/types";
import { ReactComponent as ApplePayLogo } from "assets/icons/apple-pay.svg";
import { LoadingButton } from "components/LoadingButton";
import { handleAmpliLoaded } from "helpers/handleAmpliLoaded";

export const ApplePayButton = ({ data }: { data: IxopayFormData }) => {
    const navigate = useNavigate();
    const { flowOuter, ipInfo } = useAppData();
    const { user } = useUserStore();

    const { plan, successQuery, declineQuery } = data;

    const paymentInfo: IPaymentInfo = useMemo(
        (): IPaymentInfo => ({
            environment: NODE_ENV === "production" ? "production" : "development",
            apiBaseUrl: PAYMENT_SERVER_URL,
            projectName: PROJECT_NAME,
            planFetchParams: {
                planAmplitudeId: plan?.amplitudeId ?? "",
                isTest: false,
                countryCode: ipInfo?.country || "",
                currency: "USD",
            },
            userFetchParams: {
                email: user?.email || "",
                userAgent: typeof window !== "undefined" ? window.navigator.userAgent : "",
                isLegal: !flowOuter,
                amplitudeSessionId: user?.id || "",
                countryCode: ipInfo?.country || "",
                postalCode: ipInfo?.postal,
                city: ipInfo?.city,
                ip: ipInfo?.ip || "",
                state: ipInfo?.state,
            },
            ampliConfig: { ampli },
        }),
        [plan]
    );

    const options: TAppleIxopayOptions = {
        applePayOptions: { label: "Geomobile" },
        secureFlowUrl: `${window.location.origin}${AppRoutes.SECURE_FLOW_RESULT}`,
        successCallback: () => {
            navigate({ pathname: AppRoutes.SUCCESS, search: createSearchParams({ ...successQuery, provider: "applepay" }).toString() });
        },
        failureCallback: () => {
            navigate({ pathname: AppRoutes.DECLINE, search: createSearchParams({ ...declineQuery, provider: "applepay" }).toString() });
        },
        customBtn: (props: { onClick: () => void; disabled?: boolean; loading?: boolean }) => (
            <LoadingButton
                onClick={() => {
                    handleAmpliLoaded(() => ampli.applePayButtonClicked());
                    props.onClick();
                }}
                disabled={props.disabled || false}
                className="applepay-btn"
                loading={props.loading}
                sx={{
                    width: "100%",
                    height: "56px",
                    borderRadius: "8px",
                    backgroundColor: "background.grey",
                    boxShadow: "none",
                    border: "1px solid #efefef",
                    "&:hover": {
                        backgroundColor: "text.black",
                        border: "1px solid #efefef",
                    },
                    "&:disabled": {
                        opacity: 0.2,
                        backgroundColor: "transparent",
                    },
                }}
            >
                <ApplePayLogo />
            </LoadingButton>
        ),
    };

    return <AppleIxopaySafeBtn paymentInfo={paymentInfo} options={options} />;
};
